import React, { useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Box,
  Avatar,
  Button,
  Paper
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h6: {
      fontWeight: 600,
    },
  },
});

function App() {
  useEffect(() => {
    // Create and append Dialogflow Messenger element
    const dfMessenger = document.createElement('df-messenger');
    dfMessenger.setAttribute('project-id', 'kitabisa-ai');
    dfMessenger.setAttribute('agent-id', 'f84f3538-a346-4090-a6b1-5f1a44839c6b'); // Replace with your agent ID
    dfMessenger.setAttribute('language-code', 'id');
    dfMessenger.setAttribute('max-query-length', '150');
    dfMessenger.setAttribute('storage-option', 'none'); 
    dfMessenger.setAttribute('allow-feedback', 'all'); 
    dfMessenger.setAttribute('wait-open', 'true'); 
    dfMessenger.setAttribute('intent', 'welcome'); 

    // Create chat bubble
    const chatBubble = document.createElement('df-messenger-chat');
    chatBubble.setAttribute('chat-title', 'Tanya SalingJaga');
    chatBubble.setAttribute('expanded', 'true'); 
    
    // Append elements
    dfMessenger.appendChild(chatBubble);
    document.body.appendChild(dfMessenger);
    
    // Cleanup on component unmount
    return () => {
      if (document.body.contains(dfMessenger)) {
        document.body.removeChild(dfMessenger);
      }
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              SalingJaga AI
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 8,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <Typography variant="h1" component="h1" gutterBottom>
              SalingJaga AI
            </Typography>
            <Typography variant="h5" component="p" paragraph>
              Your Intelligent Financial & Insurance Advisor
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
              Navigate your financial future with confidence using our AI-powered assistant.
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              onClick={() => {
                // Find the df-messenger-chat-bubble and click it
                const chatButton = document.querySelector('df-messenger-chat-bubble');
                if (chatButton) {
                  chatButton.click();
                }
              }}
            >
              Chat with Alin
            </Button>
          </Container>
        </Box>

        {/* Features Section */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h2" component="h2" align="center" gutterBottom>
            How We Can Help You
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {/* Feature 1 */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 } }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <AccountBalanceIcon />
                    </Avatar>
                  }
                  title="Personalized Advice"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    Tailored recommendations based on your unique financial profile and goals.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Feature 2 */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 } }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <TrendingUpIcon />
                    </Avatar>
                  }
                  title="Market Insights"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    Stay informed with real-time market trends and financial analysis.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Feature 3 */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 } }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <SecurityIcon />
                    </Avatar>
                  }
                  title="Secure & Private"
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    Your data is protected with industry-leading security measures.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Call to Action Section */}
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" component="h3" gutterBottom>
                Ready to get started?
              </Typography>
              <Typography variant="body1" paragraph>
                Ask our AI advisor about financial planning, insurance options, or investment strategies.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                onClick={() => {
                  const chatButton = document.querySelector('df-messenger-chat-bubble');
                  if (chatButton) {
                    chatButton.click();
                  }
                }}
              >
                Chat Now
              </Button>
            </Paper>
          </Container>
        </Box>

        {/* Footer */}
        <Box component="footer" sx={{ bgcolor: 'primary.dark', color: 'white', py: 3, mt: 'auto' }}>
          <Container maxWidth="lg">
            <Typography variant="body2" align="center">
              © {new Date().getFullYear()} SalingJaga AI. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
